import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getAssetBreakdown,
  getDailyProfitRange,
  getHourlyProfit,
  AssetBreakdownSummary,
  DailyProfitSummary,
} from '../api/profit';
import {
  aggregateByDayOfWeek,
  aggregateByHourOfDay,
  calculateProfitabilityMetrics,
  calculateEfficiencyMetrics,
  DayOfWeekBreakdown,
  HourOfDayBreakdown,
  ProfitabilityMetrics,
  EfficiencyMetrics,
} from '../utils/breakdownUtils';
import { DateRange } from '../utils/dateUtils';
import { useDateRangeChangeCounter } from '../stores/useDateRangeStore';
import { useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';

export interface BreakdownData {
  assetBreakdowns: AssetBreakdownSummary[];
  dayOfWeekBreakdowns: DayOfWeekBreakdown[];
  hourOfDayBreakdowns: HourOfDayBreakdown[];
  profitabilityMetrics: ProfitabilityMetrics;
  efficiencyMetrics: EfficiencyMetrics;
}

/**
 * Custom hook to fetch and calculate all breakdown data
 * Fetches asset breakdown from API and daily data for other calculations
 */
export function useBreakdownData(dateRange: DateRange) {
  const rangeChangeCounter = useDateRangeChangeCounter();
  const refreshTrigger = useAutoRefreshTrigger('profit');

  // Fetch asset breakdown from API
  const { data: assetBreakdowns = [], isLoading: isLoadingAssets, error: assetError } = useQuery({
    queryKey: ['assetBreakdown', dateRange.start_date, dateRange.end_date, rangeChangeCounter, refreshTrigger],
    queryFn: () => getAssetBreakdown(dateRange.start_date, dateRange.end_date),
    staleTime: 30000, // 30 seconds
  });

  // Fetch daily profit data for time and performance breakdowns
  const { data: dailyData = [], isLoading: isLoadingDaily, error: dailyError } = useQuery({
    queryKey: ['dailyProfitBreakdown', dateRange.start_date, dateRange.end_date, rangeChangeCounter, refreshTrigger],
    queryFn: () => getDailyProfitRange(dateRange.start_date, dateRange.end_date),
    staleTime: 30000, // 30 seconds
  });

  // Calculate time pattern breakdowns
  const dayOfWeekBreakdowns = useMemo(() => {
    return aggregateByDayOfWeek(dailyData);
  }, [dailyData]);

  // For hour of day, we need hourly data - only fetch if we have a reasonable date range
  const daysDiff = useMemo(() => {
    const start = new Date(dateRange.start_date);
    const end = new Date(dateRange.end_date);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  }, [dateRange]);

  // Only fetch hourly data if the range is not too large (e.g., <= 90 days)
  const shouldFetchHourly = daysDiff <= 90;

  const { data: hourlyDataArray = [] } = useQuery({
    queryKey: ['hourlyProfitBreakdown', dateRange.start_date, dateRange.end_date, rangeChangeCounter, refreshTrigger],
    queryFn: async () => {
      if (!shouldFetchHourly) return [];
      
      // Fetch hourly data for each day in the range
      const promises: Promise<any[]>[] = [];
      const start = new Date(dateRange.start_date);
      const end = new Date(dateRange.end_date);
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        promises.push(getHourlyProfit(dateStr));
      }
      
      const results = await Promise.all(promises);
      return results.flat();
    },
    enabled: shouldFetchHourly,
    staleTime: 30000,
  });

  const hourOfDayBreakdowns = useMemo(() => {
    return aggregateByHourOfDay(hourlyDataArray);
  }, [hourlyDataArray]);

  // Calculate performance metrics
  const profitabilityMetrics = useMemo(() => {
    return calculateProfitabilityMetrics(dailyData);
  }, [dailyData]);

  const efficiencyMetrics = useMemo(() => {
    return calculateEfficiencyMetrics(dailyData);
  }, [dailyData]);

  const isLoading = isLoadingAssets || isLoadingDaily;
  const error = assetError || dailyError;

  return {
    data: {
      assetBreakdowns,
      dayOfWeekBreakdowns,
      hourOfDayBreakdowns,
      profitabilityMetrics,
      efficiencyMetrics,
    } as BreakdownData,
    isLoading,
    error: error ? String(error) : null,
  };
}
